// src/services/referentielService.js
//
// Wrapper rétro-compatible au-dessus de `clausierService`.
//
// Avant : 4 appels HTTP (normes, paragraphes, categories, templates) + jointure côté client.
// Maintenant : 1 seul appel à `/referentiels/clausier-electricite` (avec ETag/304),
// et on aplatit l'arbre côté client pour conserver le format historique (liste plate
// de templates) attendu par tout le code existant (InspectionHub, useReferentiel, normesService).

import clausierService, { flattenNormeTemplates } from './clausierService';

class ReferentielService {
  /**
   * Liste plate des templates avec hiérarchie dénormalisée (norme_code, paragraphe_code…).
   * Format : Array<TemplateFlat>
   */
  async getReferentiel() {
    const tree = await clausierService.getTree();
    return flattenNormeTemplates(tree);
  }

  async refreshReferentiel() {
    const tree = await clausierService.refresh();
    return flattenNormeTemplates(tree);
  }

  async clearCache() {
    await clausierService.clearCache();
  }

  /**
   * Stats du cache au format attendu par `RefreshReferentielButton`.
   */
  async getCacheStats() {
    return clausierService.getCacheStats();
  }

  /**
   * Stats hiérarchiques (utilisé par certains debug screens).
   * Format historique reconstruit depuis la liste plate.
   */
  async getHierarchyStats() {
    try {
      const referentiels = await this.getReferentiel();
      if (!referentiels || referentiels.length === 0) return null;

      const domaines = [...new Set(referentiels.map((r) => r.domaine).filter(Boolean))];
      const normes = [...new Set(referentiels.map((r) => r.norme_code).filter(Boolean))];
      const paragraphes = [...new Set(referentiels.map((r) => r.paragraphe_code).filter(Boolean))];
      const categories = [...new Set(referentiels.map((r) => r.categorie_code).filter(Boolean))];

      return {
        total: referentiels.length,
        domaines: { count: domaines.length, values: domaines.sort() },
        normes: { count: normes.length, values: normes.sort() },
        paragraphes: {
          count: paragraphes.length,
          values: paragraphes.sort((a, b) => {
            const aNum = parseInt(a, 10);
            const bNum = parseInt(b, 10);
            if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
            return a.localeCompare(b);
          }),
        },
        categories: { count: categories.length, values: categories.sort() },
      };
    } catch (error) {
      console.error('❌ Erreur getHierarchyStats:', error);
      return null;
    }
  }
}

const referentielServiceInstance = new ReferentielService();
export default referentielServiceInstance;
